/**
 * The sheet editor (spec 035): cut a picture into frames (the cut lives on
 * the media item, 033 D-1) and fine-tune how this state plays it, against a
 * live preview. It works on a copy of both: Done saves a changed cut with
 * `PATCH /media/:id` first (a failure keeps the editor open with the reason
 * and applies nothing), then applies the state's change as one undo step;
 * Cancel and Escape discard both (033 D-8).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlaybackMode, SheetCut, SpriteSize, StatePlayback } from '../../../../Shared/src/@types';
import {
  DEFAULT_PLAYBACK, SHEET_MAX_CELLS, frameDurations, sheetFrameRect, sheetFrameSize,
} from '../../../../Shared/src/utils/spriteSheet';
import { apiClient } from '../../api/client';
import { useModalContext } from '../../contexts/ModalContext';
import MediaPicker from '../../modals/MediaPicker';
import { useMediaSheetStore } from '../../stores/mediaSheetStore';
import { useSceneStore } from '../../stores/sceneStore';
import { cycleInfo, playedRange } from '../../utils/playbackSpeed';
import { ModeSwitch, WhenDoneSelect, endsAtAll, withMode, withSpeed, withWhenDone } from './PlaybackControls';
import SheetFrame from './SheetFrame';
import SpeedSlider from './SpeedSlider';
import { usePicture } from './usePicture';
import { useSheetClock } from './useSheetClock';
import styles from './Sheets.module.scss';

export interface SheetEditorProps {
  spriteId: string;
  stateName: string;
  /** The picture to cut; the state's own unless `assign`. */
  mediaId: number;
  /** Done also points the state at the picture (one undo step with the playback). */
  assign?: boolean;
  onClose: () => void;
}

export const SHEET_EDITOR_TITLE = (sprite: string, state: string) => `Edit sheet · ${sprite} · ${state}`;

/**
 * The grid a picture cut for the first time starts with (D-3): a horizontal
 * strip when its width is a whole multiple (2–64) of its height, else 1 × 1;
 * every cell a frame; 12 fps.
 */
export function firstCut(natural: SpriteSize | undefined): SheetCut {
  let columns = 1;
  if (natural && natural.height > 0 && natural.width % natural.height === 0) {
    const k = natural.width / natural.height;
    if (k >= 2 && k <= SHEET_MAX_CELLS) columns = k;
  }
  return { columns, rows: 1, frameCount: columns, fps: 12, cutBy: 'grid', source: 'grid' };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const clampInt = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(Number.isFinite(v) ? v : lo)));

/** The cut with its frame count kept within the grid; a playback range kept within the frames. */
function fitRange(p: StatePlayback, cut: SheetCut): StatePlayback {
  if (!p.range) return p;
  const [a, b] = playedRange(cut, p);
  const { range: _r, ...rest } = p;
  return a === 0 && b === cut.frameCount - 1 ? rest : { ...rest, range: [a, b] };
}

const Stepper: React.FC<{ label: string; value: number; min: number; max: number; readOnly?: boolean; onChange: (v: number) => void }> = ({
  label, value, min, max, readOnly, onChange,
}) => (
  <label>
    <span>{label}</span>
    <input
      type="number"
      aria-label={label}
      value={value}
      min={min}
      max={max}
      step={1}
      readOnly={readOnly}
      onChange={(e) => { if (!readOnly && e.target.value !== '') onChange(clampInt(Number(e.target.value), min, max)); }}
    />
  </label>
);

const SheetEditor: React.FC<SheetEditorProps> = ({ spriteId, stateName, mediaId: initialMedia, assign = false, onClose }) => {
  const { addModal, removeModal } = useModalContext();
  const sprite = useSceneStore((s) => s.scene?.sprites.find((sp) => sp.id === spriteId));
  const state = sprite?.states.find((st) => st.name === stateName);

  const [mediaId, setMediaId] = useState(initialMedia);
  const picture = usePicture(mediaId);
  const [usedIn, setUsedIn] = useState<number | null>(null);

  // The copies Done applies. The cut starts from the stored one, or D-3's first grid once the size is known.
  const [cut, setCut] = useState<SheetCut | null>(null);
  const [cutFrom, setCutFrom] = useState<{ mediaId: number; cut: SheetCut | undefined } | null>(null);
  const original = state?.playback;
  const [playback, setPlayback] = useState<StatePlayback>(() => original ?? { ...DEFAULT_PLAYBACK });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!picture.known || (cutFrom && cutFrom.mediaId === mediaId)) return;
    if (picture.cut) {
      setCut(picture.cut);
      setCutFrom({ mediaId, cut: picture.cut });
    } else if (picture.natural || picture.loaded) {
      setCut(firstCut(picture.natural));
      setCutFrom({ mediaId, cut: undefined });
    }
  }, [picture.known, picture.cut, picture.natural, picture.loaded, mediaId, cutFrom]);

  useEffect(() => {
    let cancelled = false;
    setUsedIn(null);
    void apiClient.getMediaUsage(mediaId).then((res) => {
      if (!cancelled && res?.success) setUsedIn(res.data.scenes.length);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [mediaId]);

  // ── The preview ──────────────────────────────────────────────────────────
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  // Pause resumes from the frame shown; a playback change keeps the phase; Once ends and turns the button back to Play.
  const { seek } = useSheetClock(cut, playback, playing, spriteId, (at) => {
    setFrame(at.index);
    if (at.done) setPlaying(false);
  });
  /** Stop on frame `i`; Play continues from it. */
  const showFrame = (i: number) => {
    setPlaying(false);
    setFrame(i);
    seek(i);
  };

  const canvas = useRef<HTMLCanvasElement>(null);
  const image = useRef<HTMLImageElement | null>(null);
  const [decoded, setDecoded] = useState(0);
  useEffect(() => {
    if (!picture.src || typeof Image === 'undefined') return;
    const img = new Image();
    img.onload = () => setDecoded((n) => n + 1);
    img.src = picture.src;
    image.current = img;
  }, [picture.src]);
  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext?.('2d');
    if (!c || !ctx || !cut || !picture.natural) return;
    const r = sheetFrameRect(cut, frame, picture.natural);
    ctx.clearRect(0, 0, c.width, c.height);
    const k = Math.min(c.width / Math.max(1, r.w), c.height / Math.max(1, r.h));
    ctx.imageSmoothingEnabled = !cut.pixelArt;
    const img = image.current;
    if (img) {
      try {
        ctx.drawImage(img, r.x, r.y, r.w, r.h, (c.width - r.w * k) / 2, (c.height - r.h * k) / 2, r.w * k, r.h * k);
      } catch {
        /* not decoded yet */
      }
    }
  }, [cut, frame, picture.natural, decoded]);

  const [first, last] = cut ? playedRange(cut, playback) : [0, 0];
  const step = (d: 1 | -1) => {
    const n = last - first + 1;
    const at = frame < first || frame > last ? first : frame;
    showFrame(first + ((((at - first + d) % n) + n) % n));
  };

  // ── Edits ────────────────────────────────────────────────────────────────
  const setGrid = (columns: number, rows: number) => {
    if (!cut) return;
    const next: SheetCut = { ...cut, columns, rows, frameCount: columns * rows };
    setCut(next);
    setPlayback((p) => fitRange(p, next));
  };
  const setFrames = (frameCount: number) => {
    if (!cut) return;
    const next = { ...cut, frameCount: Math.min(frameCount, cut.columns * cut.rows) };
    setCut(next);
    setPlayback((p) => fitRange(p, next));
  };
  const setRange = (from: number, to: number) => {
    if (!cut) return;
    const a = clampInt(from, 1, cut.frameCount);
    const b = clampInt(Math.max(a, to), a, cut.frameCount);
    setPlayback((p) => {
      const { range: _r, ...rest } = p;
      return a === 1 && b === cut.frameCount ? rest : { ...rest, range: [a - 1, b - 1] };
    });
    setFrame(a - 1);
  };
  const setMode = (mode: PlaybackMode) => setPlayback((p) => withMode(p, mode));

  const replace = useCallback(() => {
    addModal({
      title: 'Replace sheet',
      size: 'lg',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={(id: number) => {
            removeModal();
            setCut(null);
            setCutFrom(null);
            setMediaId(id);
          }}
          onCancel={() => removeModal()}
        />
      ),
    });
  }, [addModal, removeModal]);

  const done = async () => {
    if (!cut || !sprite || !state) return;
    setBusy(true);
    setError(null);
    const cutChanged = !cutFrom || !same(cutFrom.cut, cut);
    if (cutChanged) {
      let res;
      try {
        res = await apiClient.updateMedia(mediaId, { sheet: cut });
      } catch (err) {
        res = { success: false as const, error: err instanceof Error ? err.message : String(err) };
      }
      if (!res?.success) {
        setBusy(false);
        setError(`The cut couldn't be saved: ${(res && 'error' in res && res.error) || 'try again'}`);
        return;
      }
      useMediaSheetStore.getState().setSheet(mediaId, cut);
    }
    const next = same(playback, DEFAULT_PLAYBACK) && original === undefined ? undefined : playback;
    const playbackChanged = !same(next, original);
    const repointed = assign || mediaId !== state.mediaId;
    let refusal: string | null = null;
    if (repointed) {
      refusal = useSceneStore.getState().applyStateSheet(spriteId, stateName, {
        mediaId,
        ...(picture.natural ? { naturalSize: picture.natural } : {}),
        ...(playbackChanged ? { playback: next ?? null } : {}),
      });
    } else if (playbackChanged) {
      refusal = useSceneStore.getState().setStatePlayback(spriteId, stateName, next);
    }
    setBusy(false);
    if (refusal) {
      setError(refusal);
      return;
    }
    onClose();
  };

  const durations = useMemo(() => (cut ? frameDurations(cut, playback.speed) : []), [cut, playback.speed]);
  if (!sprite || !state) return <p className={styles.error}>That state no longer exists.</p>;

  const json = cut?.cutBy === 'json';
  const frameSize = cut && picture.natural ? sheetFrameSize(cut, picture.natural) : undefined;
  const info = cut ? cycleInfo(cut, playback) : null;
  const ownFps = cut ? cycleInfo(cut, { mode: 'loop' }) : null;
  const shownW = picture.natural?.width ?? 0;
  const shownH = picture.natural?.height ?? 0;

  return (
    <div className={styles.editor} data-testid="sheet-editor">
      <section className={styles.pane} aria-label="Cut">
        <h4>Cut · on the picture</h4>
        <div className={styles.row}>
          <strong data-testid="sheet-editor-file">{picture.name ?? `Picture #${mediaId}`}</strong>
          {picture.natural && <span className={styles.muted}>{shownW} × {shownH}</span>}
          {usedIn !== null && <span className={styles.muted} data-testid="sheet-editor-usage">Used in {usedIn} {usedIn === 1 ? 'scene' : 'scenes'}</span>}
        </div>
        {cut && (
          <div className={styles.sheetView}>
            {picture.src && <img src={picture.src} alt="" style={{ imageRendering: cut.pixelArt ? 'pixelated' : undefined }} />}
            <div
              className={styles.grid}
              style={{ gridTemplateColumns: `repeat(${cut.columns}, 1fr)`, gridTemplateRows: `repeat(${cut.rows}, 1fr)` }}
            >
              {Array.from({ length: cut.columns * cut.rows }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={i < cut.frameCount ? `Frame ${i + 1}` : `Empty cell ${i + 1}`}
                  className={`${styles.cell} ${i >= cut.frameCount ? styles.empty : ''} ${i === frame ? styles.current : ''}`}
                  data-testid="sheet-cell"
                  data-empty={i >= cut.frameCount ? 'true' : undefined}
                  disabled={i >= cut.frameCount}
                  onClick={() => showFrame(i)}
                />
              ))}
            </div>
          </div>
        )}
        {cut && (
          <>
            <div className={styles.steppers}>
              <Stepper label="Columns" value={cut.columns} min={1} max={SHEET_MAX_CELLS} readOnly={json} onChange={(v) => setGrid(v, cut.rows)} />
              <Stepper label="Rows" value={cut.rows} min={1} max={SHEET_MAX_CELLS} readOnly={json} onChange={(v) => setGrid(cut.columns, v)} />
              <Stepper label="Frames" value={cut.frameCount} min={1} max={cut.columns * cut.rows} readOnly={json} onChange={setFrames} />
            </div>
            {json ? (
              <p className={styles.muted} data-testid="sheet-editor-note">
                From the sheet&apos;s JSON: {cut.frameCount} frames, each cut at its own position. The grid is shown for reference.
              </p>
            ) : (
              <p className={styles.muted} data-testid="sheet-editor-note">
                {frameSize ? `Each frame is ${frameSize.width} × ${frameSize.height}, ` : ''}read left to right, then top to bottom.
              </p>
            )}
            <div className={styles.row}>
              {json && (
                <button type="button" className={styles.button} onClick={() => setCut({ ...cut, cutBy: 'grid' })}>Cut by grid instead</button>
              )}
              {!json && cut.json && cut.json.frames.length > 0 && (
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    const next: SheetCut = { ...cut, cutBy: 'json', frameCount: cut.json!.frames.length, columns: Math.max(cut.columns, 1), rows: Math.max(cut.rows, 1) };
                    if (next.frameCount > next.columns * next.rows) next.columns = Math.min(SHEET_MAX_CELLS, next.frameCount);
                    if (next.frameCount > next.columns * next.rows) next.rows = Math.ceil(next.frameCount / next.columns);
                    setCut(next);
                    setPlayback((p) => fitRange(p, next));
                  }}
                >
                  Use the JSON again
                </button>
              )}
              <label className={styles.row}>
                <input type="checkbox" checked={!!cut.pixelArt} onChange={(e) => {
                  const { pixelArt: _p, ...rest } = cut;
                  setCut(e.target.checked ? { ...rest, pixelArt: true } : rest);
                }} />
                <span>Crisp pixels (pixel art)</span>
              </label>
            </div>
          </>
        )}
        {!cut && <p className={styles.muted}>Reading the picture…</p>}
      </section>

      <section className={styles.pane} aria-label="Playback">
        <h4>Playback · this state</h4>
        <canvas ref={canvas} width={200} height={200} className={styles.preview} data-testid="sheet-editor-preview" data-frame={frame} />
        <div className={styles.row}>
          <button type="button" className={styles.button} aria-label="Previous frame" onClick={() => step(-1)} disabled={!cut}>‹</button>
          <button type="button" className={styles.button} aria-label={playing ? 'Pause' : 'Play'} onClick={() => setPlaying((p) => !p)} disabled={!cut}>
            {playing ? '❚❚' : '▶'}
          </button>
          <button type="button" className={styles.button} aria-label="Next frame" onClick={() => step(1)} disabled={!cut}>›</button>
          <span data-testid="sheet-editor-frame">Frame {frame + 1} / {cut?.frameCount ?? 0}</span>
        </div>
        <SpeedSlider
          variant="detail"
          scale
          speed={playback.speed}
          onChange={(v) => setPlayback((p) => withSpeed(p, v))}
          testId="sheet-editor-speed"
        />
        {cut && info && ownFps && (
          <p className={styles.muted} data-testid="sheet-editor-rate">
            The cut runs at {Number(ownFps.fps.toFixed(1))} fps ({Math.round(1000 / Math.max(1e-6, ownFps.fps))} ms per frame).
            {' '}Here: {Number(info.fps.toFixed(1))} fps, {Number(info.seconds.toFixed(2))} s per cycle.
          </p>
        )}
        <ModeSwitch mode={playback.mode} onChange={setMode} />
        {cut && (
          <div className={styles.steppers}>
            <Stepper label="Play from frame" value={first + 1} min={1} max={cut.frameCount} onChange={(v) => setRange(v, Math.max(v, last + 1))} />
            <Stepper label="To frame" value={last + 1} min={first + 1} max={cut.frameCount} onChange={(v) => setRange(first + 1, v)} />
          </div>
        )}
        {endsAtAll(playback) && (
          <WhenDoneSelect playback={playback} onChange={(w) => setPlayback((p) => withWhenDone(p, w))} />
        )}
        <label className={styles.row}>
          <input
            type="checkbox"
            checked={!!playback.randomStart}
            onChange={(e) => setPlayback((p) => {
              const { randomStart: _r, ...rest } = p;
              return e.target.checked ? { ...rest, randomStart: true } : rest;
            })}
          />
          <span>Start on a random frame</span>
        </label>
      </section>

      {cut && (
        <div className={styles.strip} data-testid="sheet-editor-strip">
          {durations.map((ms, i) => (
            <button
              key={i}
              type="button"
              className={`${styles.stripFrame} ${i < first || i > last ? styles.dimmed : ''} ${i === frame ? styles.current : ''}`}
              data-testid="sheet-strip-frame"
              data-dimmed={i < first || i > last ? 'true' : undefined}
              onClick={() => showFrame(i)}
            >
              <SheetFrame src={picture.src} natural={picture.natural} cut={cut} index={i} size={40} />
              <span>{i + 1}</span>
              <span data-testid="sheet-strip-ms">{Math.round(ms)} ms</span>
            </button>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.button} onClick={replace} disabled={busy}>Replace sheet…</button>
        <span className={styles.spacer} />
        {error && <span className={styles.error} role="alert" data-testid="sheet-editor-error">{error}</span>}
        <button type="button" className={styles.button} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={`${styles.button} ${styles.primary}`} onClick={() => void done()} disabled={busy || !cut} data-testid="sheet-editor-done">
          Done
        </button>
      </div>
    </div>
  );
};

export default SheetEditor;
